// src/pages/common/MyAttendance.jsx

import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useCollection,
  formatDate,
  formatTime,
  getTodayStr,
} from "../../hooks/useFirestore";
import { useHolidays } from "../../hooks/useFirestoreExtensions";
import { where, orderBy } from "firebase/firestore";
import Layout from "../../components/common/Layout";
import {
  ADMIN_NAV,
  LEAD_NAV,
  EMPLOYEE_NAV,
} from "../../utils/navigation";
import { isPresent, isWorking, isCheckedOut } from "../../utils/attendanceStatus";
import { calculateEmployeeStatsForRange } from "../../utils/statusCalculations";

export default function MyAttendance() {
  const { userProfile, isAdmin, isLead } = useAuth();

  const uid = userProfile?.uid;

  const navItems =
    isAdmin
      ? ADMIN_NAV
      : isLead
      ? LEAD_NAV
      : EMPLOYEE_NAV;

  const panelLabel =
    isAdmin
      ? "Admin Panel"
      : isLead
      ? "Team Lead Panel"
      : "Employee Portal";

  // ── Filters ─────────────────────────────────
  const [dateRange, setDateRange] =
    useState("month");

  const [customStart, setCustomStart] =
    useState("");

  const [customEnd, setCustomEnd] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("all");

  const [searchTerm] =
    useState("");

  const [exportType, setExportType] =
    useState("excel");

  const [dateError, setDateError] = useState("");
  const [searchError, setSearchError] = useState("");

  // Validate custom date range
  useEffect(() => {
    if (dateRange === "custom") {
      const todayStr = getTodayStr();
      if (customStart && customStart > todayStr) {
        setDateError("From Date cannot be in the future.");
      } else if (customEnd && customEnd > todayStr) {
        setDateError("To Date cannot be in the future.");
      } else if (customStart && customEnd && customStart > customEnd) {
        setDateError("To Date cannot be earlier than From Date.");
      } else {
        setDateError("");
      }
    } else {
      setDateError("");
    }
  }, [dateRange, customStart, customEnd]);

  // Validate search input (DEF-11)
  useEffect(() => {
    if (!searchTerm) {
      setSearchError("");
      return;
    }
    const trimmed = searchTerm.trim();
    
    // Check for negative numbers or standalone negative signs (not part of dates)
    const hasNegative = /-\d+/.test(trimmed) || (trimmed.startsWith("-") && !/^\d{4}-\d{2}-\d{2}$/.test(trimmed));
    // Check for invalid characters (allow letters, numbers, spaces, hyphens, and slashes)
    const hasSpecial = /[^A-Za-z0-9\s\-/]/.test(trimmed);
    // Malformed formats (consecutive hyphens/slashes/mixed)
    const malformed = /--|\/\/|-\/|\/-/.test(trimmed);

    if (hasNegative) {
      setSearchError("Invalid date. Negative values are not allowed.");
    } else if (hasSpecial || malformed) {
      setSearchError("Invalid date. Special characters and malformed formats are not allowed.");
    } else {
      setSearchError("");
    }
  }, [searchTerm]);

  // ── Attendance Fetch ───────────────────────
  const attendanceConstraints = useMemo(
    () => [
      where("userId", "==", uid || "none"),
      orderBy("date", "desc"),
    ],
    [uid]
  );

  const {
    data: attendance,
    loading: attLoading,
  } = useCollection(
    "attendance",
    attendanceConstraints
  );

  // ── Leave Fetch ────────────────────────────
  const leaveConstraints = useMemo(
    () => [
      where("userId", "==", uid || "none"),
      orderBy("fromDate", "desc"),
    ],
    [uid]
  );

  const {
    data: leaves,
    loading: leaveLoading,
  } = useCollection(
    "leaveRequests",
    leaveConstraints
  );

  const {
    data: holidays,
    loading: holLoading,
  } = useHolidays();

  // ── Filter Logic ───────────────────────────
  const filteredData = useMemo(() => {
    if (searchError) {
      return {
        attendance: [],
        leaves: [],
        holidays: [],
        absentDates: [],
        leaveDates: [],
        stats: { present: 0, absent: 0, leaves: 0, rate: 0 }
      };
    }
    let start, end;

    const now = new Date();

    if (dateRange === "today") {
      const today = new Date()
        .toISOString()
        .split("T")[0];

      start = today;
      end = today;
    } else if (dateRange === "week") {
      const day = now.getDay();

      const diff =
        now.getDate() -
        day +
        (day === 0 ? -6 : 1);

      const monday = new Date(
        now.setDate(diff)
      );

      start = monday
        .toISOString()
        .split("T")[0];

      end = new Date()
        .toISOString()
        .split("T")[0];
    } else if (dateRange === "month") {
      start = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-01`;

      end = new Date()
        .toISOString()
        .split("T")[0];
    } else {
      start = customStart;
      end = customEnd;
    }

    const todayStr = getTodayStr();

    if (dateRange === "custom") {
      if (customStart && customStart > todayStr) {
        return {
          attendance: [],
          leaves: [],
          holidays: [],
          absentDates: [],
          leaveDates: [],
          stats: { present: 0, absent: 0, leaves: 0, rate: 0 }
        };
      }
      if (customEnd && customEnd > todayStr) {
        return {
          attendance: [],
          leaves: [],
          holidays: [],
          absentDates: [],
          leaveDates: [],
          stats: { present: 0, absent: 0, leaves: 0, rate: 0 }
        };
      }
      if (customStart && customEnd && customStart > customEnd) {
        return {
          attendance: [],
          leaves: [],
          holidays: [],
          absentDates: [],
          leaveDates: [],
          stats: { present: 0, absent: 0, leaves: 0, rate: 0 }
        };
      }
    }

    if (!start)
      return {
        attendance: [],
        leaves: [],
        holidays: [],
        absentDates: [],
        leaveDates: [],
        stats: { present: 0, absent: 0, leaves: 0, rate: 0 }
      };

    const effectiveEnd = end || todayStr;

    const rangeAttendance = attendance.filter(
      (a) =>
        a.date >= start &&
        a.date <= effectiveEnd
    );

    const rangeLeaves = leaves.filter(
      (l) =>
        l.fromDate <= effectiveEnd && l.toDate >= start
    );

    const rangeHolidays = holidays.filter(
      (h) =>
        h.date >= start &&
        h.date <= effectiveEnd
    );

    // Build helper sets for checking status
    const checkedInDates = new Set(
      rangeAttendance.filter(isPresent).map((a) => a.date)
    );

    const holidayDates = new Set(rangeHolidays.map((h) => h.date));

    // Resolve leaves to individual dates by status
    const approvedLeaveDates = new Set();
    const pendingLeaveDates = new Set();
    const rejectedLeaveDates = new Set();

    rangeLeaves.forEach((l) => {
      let d = new Date(l.fromDate + "T00:00:00");
      const to = new Date(l.toDate + "T00:00:00");
      while (d <= to) {
        const dateStr = d.toISOString().split("T")[0];
        if (dateStr >= start && dateStr <= effectiveEnd) {
          if (l.status === "approved") {
            approvedLeaveDates.add(dateStr);
          } else if (l.status === "pending_lead" || l.status === "pending_admin") {
            pendingLeaveDates.add(dateStr);
          } else if (l.status === "rejected" || l.status === "cancelled") {
            rejectedLeaveDates.add(dateStr);
          }
        }
        d.setDate(d.getDate() + 1);
      }
    });

    // Helper for formatting local dates safely without timezone shift
    const formatDateLocal = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    const absentDates = [];
    const leaveDates = [];
    const pendingLeaveList = [];
    const rejectedLeaveList = [];

    let curr = new Date(start + "T00:00:00");
    const stop = new Date(effectiveEnd + "T00:00:00");

    while (curr <= stop) {
      const dateStr = formatDateLocal(curr);
      const dayOfWeek = curr.getDay();

      // Only count working days (Mon-Fri) that are not holidays
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidayDates.has(dateStr)) {
        if (!checkedInDates.has(dateStr)) {
          if (approvedLeaveDates.has(dateStr)) {
            leaveDates.push({
              id: `leave_${dateStr}`,
              date: dateStr,
              status: "Leave",
              isLeave: true,
              checkIn: null,
              checkOut: null,
              hoursWorked: 0,
              mode: "—",
              isLate: false,
            });
          } else if (pendingLeaveDates.has(dateStr)) {
            pendingLeaveList.push({
              id: `pending_leave_${dateStr}`,
              date: dateStr,
              status: "Pending Leave",
              isPendingLeave: true,
              checkIn: null,
              checkOut: null,
              hoursWorked: 0,
              mode: "—",
              isLate: false,
            });
          } else if (rejectedLeaveDates.has(dateStr)) {
            rejectedLeaveList.push({
              id: `rejected_leave_${dateStr}`,
              date: dateStr,
              status: "Rejected/Cancelled Leave",
              isRejectedLeave: true,
              checkIn: null,
              checkOut: null,
              hoursWorked: 0,
              mode: "—",
              isLate: false,
            });
          } else if (dateStr <= todayStr) {
            absentDates.push({
              id: `absent_${dateStr}`,
              date: dateStr,
              status: "Absent",
              isAbsent: true,
              checkIn: null,
              checkOut: null,
              hoursWorked: 0,
              mode: "—",
              isLate: false,
            });
          }
        }
      }
      curr.setDate(curr.getDate() + 1);
    }

    // Sort generated arrays descending by date
    absentDates.sort((a, b) => b.date.localeCompare(a.date));
    leaveDates.sort((a, b) => b.date.localeCompare(a.date));
    pendingLeaveList.sort((a, b) => b.date.localeCompare(a.date));
    rejectedLeaveList.sort((a, b) => b.date.localeCompare(a.date));

    // Calculate KPI stats based on range-filtered data
    const presentDaysCount = rangeAttendance.filter(isPresent).length;
    
    // Number of approved leave days strictly within the selected date range
    const approvedLeavesCount = leaveDates.length;

    const absentDaysCount = absentDates.length;

    // Total working days
    let workingDaysCount = 0;
    let tempCurr = new Date(start + "T00:00:00");
    const tempStop = new Date(effectiveEnd + "T00:00:00");
    while (tempCurr <= tempStop) {
      const day = tempCurr.getDay();
      const dateStr = formatDateLocal(tempCurr);
      if (day !== 0 && day !== 6 && !holidayDates.has(dateStr)) {
        workingDaysCount++;
      }
      tempCurr.setDate(tempCurr.getDate() + 1);
    }

    const attendanceRate =
      workingDaysCount > 0
        ? Math.round((presentDaysCount / workingDaysCount) * 100)
        : 0;
    // Calculate KPI stats using consistent range stats helper
    const stats = calculateEmployeeStatsForRange({
      attendanceRecords: rangeAttendance,
      leaveRequests: rangeLeaves,
      holidays: rangeHolidays,
      startDate: start,
      endDate: effectiveEnd,
      todayStr,
    });

    let att = rangeAttendance;

    // Combine real attendance with synthetic records for unified handling
    const combinedAttendance = [
      ...rangeAttendance,
      ...absentDates,
      ...leaveDates,
      ...pendingLeaveList,
      ...rejectedLeaveList,
    ];

    // Status Filter
    if (statusFilter !== "all") {
      if (statusFilter === "present") {
        att = combinedAttendance.filter(isPresent);
      } else if (statusFilter === "absent") {
        att = combinedAttendance.filter((a) => a.isAbsent);
      } else if (statusFilter === "checked-out") {
        att = combinedAttendance.filter(isCheckedOut);
      } else if (statusFilter === "leave") {
        att = combinedAttendance.filter((a) => a.isLeave);
      } else if (statusFilter === "pending-leave") {
        att = combinedAttendance.filter((a) => a.isPendingLeave);
      } else if (statusFilter === "rejected-leave") {
        att = combinedAttendance.filter((a) => a.isRejectedLeave);
      } else if (statusFilter === "late") {
        att = combinedAttendance.filter((a) => a.isLate);
      } else {
        att = combinedAttendance.filter((a) => a.status === statusFilter);
      }
    } else {
      att = combinedAttendance;
    }

    // Search Filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      att = att.filter((a) => {
        const rawDate = (a.date || "").toLowerCase();
        
        // Formatted date string in table: e.g. "19 Jun 2026"
        let formattedDate = "";
        if (a.date && typeof a.date === "string" && a.date.includes("-")) {
          const parts = a.date.split("-");
          if (parts.length === 3) {
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const day = parseInt(parts[2], 10);
            const monthIndex = parseInt(parts[1], 10) - 1;
            const year = parts[0];
            if (monthIndex >= 0 && monthIndex < 12) {
              formattedDate = `${day} ${months[monthIndex]} ${year}`.toLowerCase();
            }
          }
        }
        
        const stdFormatted = formatDate(a.date).toLowerCase();
        
        return rawDate.includes(term) || formattedDate.includes(term) || stdFormatted.includes(term);
      });
    }

    return {
      attendance: att,
      leaves: rangeLeaves,
      holidays: rangeHolidays,
      start,
      end,
      stats,
    };
  }, [
    attendance,
    leaves,
    holidays,
    dateRange,
    customStart,
    customEnd,
    statusFilter,
    searchTerm,
    searchError,
  ]);

  // ── Stats ──────────────────────────────────
  const stats = useMemo(() => {
    return filteredData.stats || {
      present: 0,
      absent: 0,
      leaves: 0,
      rate: 0,
    };
  }, [filteredData]);

  // ── Excel Export ───────────────────────────
  const exportCSV = () => {
    const headers = [
      "Date",
      "Punch-In",
      "Punch-Out",
      "Hours",
      "Mode",
      "Status",
    ];

    const rows =
      filteredData.attendance.map((a) => [
        a.date,
        formatTime(a.checkIn),
        formatTime(a.checkOut),
        a.hoursWorked || 0,
        a.mode,
        a.status,
      ]);

    const csvContent = [headers, ...rows]
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link =
      document.createElement("a");

    const url =
      URL.createObjectURL(blob);

    link.setAttribute("href", url);

    link.setAttribute(
      "download",
      `attendance_history_${uid}.csv`
    );

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  // ── PDF Export ─────────────────────────────
  const exportPDF = () => {
    window.print();
  };

  const loading =
    attLoading ||
    leaveLoading ||
    holLoading;

  return (
    <Layout
      navItems={navItems}
      panelLabel={panelLabel}
    >
      <div className="page-header">
        <div>
          <h1 className="page-title">
            My Attendance
          </h1>

          <p className="page-subtitle">
            Track attendance history and
            analytics
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "0.75rem",
          }}
        >
          <select
            className="filter-select"
            value={exportType}
            onChange={(e) =>
              setExportType(
                e.target.value
              )
            }
          >
            <option value="excel">
              Export Excel
            </option>

            <option value="pdf">
              Export PDF
            </option>
          </select>

          <button
  className="btn-primary"
  onClick={() => {
    if (exportType === "pdf") {
      exportPDF();
    } else {
      exportCSV();
    }
  }}
  style={{
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  }}
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    width="18"
    height="18"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
    />
  </svg>

  Download
</button>
        </div>
      </div>

      {/* KPI */}
      <div className="kpi-grid">
        <div className="kpi-card kpi-card--blue">
  <div className="kpi-icon">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="24"
      height="24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
      />
    </svg>
  </div>

  <div className="kpi-body">
    <span className="kpi-value">{stats.present}</span>

    <span className="kpi-label">
      Days Present
    </span>
  </div>
</div>

<div className="kpi-card kpi-card--red">
  <div className="kpi-icon">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="24"
      height="24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18 18 6M6 6l12 12"
      />
    </svg>
  </div>

  <div className="kpi-body">
    <span className="kpi-value">{stats.absent}</span>

    <span className="kpi-label">
      Days Absent
    </span>
  </div>
</div>

<div className="kpi-card kpi-card--amber">
  <div className="kpi-icon">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="24"
      height="24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
      />
    </svg>
  </div>

  <div className="kpi-body">
    <span className="kpi-value">{stats.leaves}</span>

    <span className="kpi-label">
      Leaves Taken
    </span>
  </div>
</div>

<div className="kpi-card kpi-card--green">
  <div className="kpi-icon">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      width="24"
      height="24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z"
      />
    </svg>
  </div>

  <div className="kpi-body">
    <span className="kpi-value">
      {stats.rate}%
    </span>

    <span className="kpi-label">
      Attendance Rate
    </span>
  </div>
</div>
      </div>

      {/* FILTERS + HISTORY */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">
            Attendance History
          </h3>
        </div>
        {/* Filters */}
        <div className="attendance-toolbar" style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "0.5rem" }}>
          <div className="attendance-filters">
            <select
              className="form-input"
              value={dateRange}
              onChange={(e) =>
                setDateRange(
                  e.target.value
                )
              }
            >
              <option value="today">
                Today
              </option>

              <option value="week">
                This Week
              </option>

              <option value="month">
                This Month
              </option>

              <option value="custom">
                Custom Range
              </option>
            </select>

            <select
              className="form-input"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value
                )
              }
            >
              <option value="all">
                All Status
              </option>

              <option value="present">
                Present
              </option>

              <option value="absent">
                Absent
              </option>

              <option value="leave">
                Leave
              </option>

              <option value="late">
                Late
              </option>
            </select>

            <h3 className="select-date-heading">Select Date</h3>

            {dateRange ===
              "custom" && (
              <>
                <input
                  type="date"
                  className="form-input"
                  value={customStart}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) =>
                    setCustomStart(
                      e.target.value
                    )
                  }
                />

                <input
                  type="date"
                  className="form-input"
                  value={customEnd}
                     max={new Date().toISOString().split("T")[0]}
                  onChange={(e) =>
                    setCustomEnd(
                      e.target.value
                    )
                  }
                />
              </>
            )}
          </div>
          {dateError && (
            <div className="date-validation-error" style={{ color: "var(--red)", fontSize: "0.875rem", fontWeight: "500", marginTop: "0.25rem", paddingLeft: "0.25rem" }}>
              ⚠️ {dateError}
            </div>
          )}
        </div>        {/* Table */}
        {loading ? (
          <div className="card-loading">
            Loading...
          </div>
        ) : filteredData.attendance
            .length === 0 ? (
          <div className="card-empty">
            No attendance records found.
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Punch-In</th>
                  <th>Punch-Out</th>
                  <th>Hours</th>
                  <th>Mode</th>
                  <th>Status</th>
                  <th>Late?</th>
                </tr>
              </thead>

              <tbody>
                {filteredData.attendance.map(
                  (a) => (
                    <tr key={a.id}>
                      <td>
                        {formatDate(
                          a.date
                        )}
                      </td>

                      <td>
                        {formatTime(
                          a.checkIn
                        )}
                      </td>

                      <td>
                        {formatTime(
                          a.checkOut
                        )}
                      </td>

                      <td>
                        {a.hoursWorked
                          ? `${a.hoursWorked.toFixed(
                              1
                            )}h`
                          : "—"}
                      </td>

                      <td>
                        {a.mode}
                      </td>

                      <td>
                        <span
                          className={`status-pill status-pill--${
                            a.isLeave
                              ? "amber"
                              : a.isPendingLeave
                              ? "amber"
                              : isWorking(a)
                              ? "green"
                              : isCheckedOut(a)
                              ? "blue"
                              : a.isAbsent
                              ? "red"
                              : a.isRejectedLeave
                              ? "gray"
                              : !isPresent(a)
                              ? "red"
                              : "gray"
                          }`}
                        >
                          {isCheckedOut(a) && (a.status === "AUTO-CHECKED-OUT" || a.status === "auto-checked-out")
                            ? "Auto Checked-Out"
                            : isCheckedOut(a)
                            ? "Checked-Out"
                            : isWorking(a)
                            ? "Working"
                            : a.status}
                        </span>
                      </td>

                      <td>
                        {!isPresent(a) ? (
                          "—"
                        ) : a.isLate ? (
                          <span className="status-pill status-pill--red">
                            +{a.lateByMinutes}m
                          </span>
                        ) : (
                          <span className="status-pill status-pill--green">
                            On Time
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}